import { NativeAdoption } from './NativeAdoption';
import { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Popconfirm,
  message,
  Tag,
  Alert,
  InputNumber,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  BarChartOutlined,
  ExperimentOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { PLATFORM_LABELS } from '../../utils/constants';
import { semverRule } from '../../utils/validators';
import { authFetch } from '../../utils/api';
import { useViewScope } from '../../utils/viewScope';
import {
  targetBrandFromRow,
  targetBrandHeader,
} from '../../shared/form/writeTargetBrand';
import { getBrandLabel, type Brand } from '../../utils/tokenManager';

const { Title, Text } = Typography;

interface VersionRecord {
  id: number;
  /**
   * 去品牌化 M4：這筆版本設定 row 的實際品牌（後端清單回傳 vpn_app_version.vpn_brand）。
   * 編輯 / 刪除 = 既有 row 寫入，目標品牌 = row.brand，顯式帶 X-Target-Brand，
   * 絕不繼承檢視範圍 / activeBrand。
   */
  brand: Brand;
  platform: string;
  minVersion: string;
  recommendedVersion: string;
  latestVersion: string;
  updateUrl: string | null;
  updateMessage: string | null;
  forceUpdateMessage: string | null;
}

interface DistributionRow {
  appVersionName: string | null;
  appVersionCode: number | null;
  uniqueUsers: number;
  eventCount: number;
}

interface WhatIfResult {
  candidateMinVersion: string;
  totalActiveUsers: number;
  forcedUserCount: number;
  forcedUserPercent: number;
  breakdownByVersion: DistributionRow[];
}

/**
 * 版本管理頁面 — 顯示 6 個平台的版本設定，支援編輯
 */
export const VersionList: React.FC = () => {
  // 檢視範圍（去品牌化 M1）：版本清單跟著 Header 檢視走。
  const { brandForRequest, isAllScope } = useViewScope();
  const [adoptionRecord, setAdoptionRecord] = useState<VersionRecord | null>(null);
  useEffect(() => { setAdoptionRecord(null); }, [brandForRequest]);
  const [data, setData] = useState<VersionRecord[]>([]);
  const [loading, setLoading] = useState(false);

  // 編輯 Modal
  const [editOpen, setEditOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [editLoading, setEditLoading] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [editRecord, setEditRecord] = useState<VersionRecord | null>(null);

  // P3-5 Active version distribution (per platform)
  const [distOpen, setDistOpen] = useState(false);
  const [distLoading, setDistLoading] = useState(false);
  const [distPlatform, setDistPlatform] = useState<string>('android');
  const [distRows, setDistRows] = useState<DistributionRow[]>([]);
  const [distWindowDays, setDistWindowDays] = useState<number>(7);

  // P3-5 What-if preview state
  const [whatIfLoading, setWhatIfLoading] = useState(false);
  const [whatIfResult, setWhatIfResult] = useState<WhatIfResult | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/admin/app-versions');
      const result = await res.json();
      if (result.code === 0) {
        setData(Array.isArray(result.data) ? result.data : []);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
    // brandForRequest 進 deps：檢視範圍變更時重拉版本清單。
  }, [brandForRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * P3-5 Enhancement #1 — fetch active version distribution
   * 給 admin 在 force-update 前查「現役 client 版本長尾」。
   */
  const fetchDistribution = useCallback(
    async (platform: string, windowDays: number) => {
      setDistLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('platform', platform);
        params.set('windowDays', String(windowDays));
        const res = await authFetch(
          `/api/admin/app-versions/distribution?${params.toString()}`
        );
        const result = await res.json();
        if (result.code === 0 && result.data?.distribution) {
          setDistRows(result.data.distribution as DistributionRow[]);
        } else {
          message.error(result.msg || '取得版本分佈失敗');
        }
      } catch {
        message.error('取得版本分佈失敗');
      } finally {
        setDistLoading(false);
      }
    },
    []
  );

  /**
   * P3-5 Enhancement #2 — what-if preview
   * candidate minVersion 會影響多少 active user (>=0%, <=100%)。
   */
  const previewWhatIf = useCallback(
    async (platform: string, candidateMinVersion: string) => {
      if (!/^\d+\.\d+\.\d+$/.test(candidateMinVersion)) {
        setWhatIfResult(null);
        return;
      }
      setWhatIfLoading(true);
      try {
        const res = await authFetch('/api/admin/app-versions/what-if', {
          method: 'POST',
          body: JSON.stringify({
            platform,
            candidateMinVersion,
            windowDays: 7,
          }),
        });
        const result = await res.json();
        if (result.code === 0 && result.data) {
          setWhatIfResult(result.data as WhatIfResult);
        } else {
          setWhatIfResult(null);
        }
      } catch {
        setWhatIfResult(null);
      } finally {
        setWhatIfLoading(false);
      }
    },
    []
  );

  const openDist = (platform: string) => {
    setDistPlatform(platform);
    setDistOpen(true);
    fetchDistribution(platform, distWindowDays);
  };

  const openEdit = (record: VersionRecord) => {
    setEditId(record.id);
    setEditRecord(record);
    setWhatIfResult(null);
    editForm.setFieldsValue(record);
    setEditOpen(true);
  };

  const handleDelete = async (record: VersionRecord) => {
    // M4：版本設定屬品牌隔離寫入。清單回傳每筆 row 的 brand 欄 → 既有 row 寫入的
    // 目標品牌 = row.brand（顯式 X-Target-Brand），絕不繼承檢視範圍 / activeBrand。
    // 對齊 backend resolveExistingRowWriteTargetBrand（顯式與 row 不符 → 403、缺顯式
    // enforce ON → 400）。
    const t = targetBrandFromRow(record.brand);
    if (!t.ok) {
      message.warning(t.reason);
      return;
    }
    try {
      const res = await authFetch(`/api/admin/app-versions/${record.id}`, {
        method: 'DELETE',
        headers: targetBrandHeader(t.brand),
      });
      const result = await res.json();
      if (result.code === 0) {
        message.success('版本設定已刪除');
        fetchData();
      } else {
        message.error(result.msg || '刪除失敗');
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  const handleEdit = async () => {
    // M4：版本更新為既有 row 寫入，目標品牌 = 該 row 的實際品牌（editRecord.brand，
    // 清單已帶 brand 欄），顯式帶 X-Target-Brand + body.targetBrand，絕不繼承檢視範圍
    // / activeBrand。缺 row.brand（理論上不會發生）→ fail-closed 擋下。
    if (!editRecord) {
      message.warning('找不到要更新的版本記錄，請重新整理列表後再操作。');
      return;
    }
    const t = targetBrandFromRow(editRecord.brand);
    if (!t.ok) {
      message.warning(t.reason);
      return;
    }
    try {
      const values = await editForm.validateFields();
      setEditLoading(true);
      const res = await authFetch(`/api/admin/app-versions/${editId}`, {
        method: 'PUT',
        headers: targetBrandHeader(t.brand),
        body: JSON.stringify({ ...values, targetBrand: t.brand }),
      });
      const result = await res.json();
      if (result.code === 0) {
        message.success('版本設定已更新');
        setEditOpen(false);
        fetchData();
      } else {
        message.error(result.msg || '更新失敗');
      }
    } catch {
      // validation error
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="page-enter">
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }} data-testid="versions-title">版本管理</Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          編輯前可先看「現役 client 版本分佈」評估推 force-update 的衝擊範圍 (P3-5)
        </Text>
      </div>

      {isAllScope && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          data-testid="versions-aggregate-notice"
          message="目前為「全部品牌（彙總）」檢視"
          description="版本設定的編輯 / 刪除屬品牌隔離寫入，請先在右上角「檢視範圍」切換到單一品牌，再進行編輯或刪除。"
        />
      )}

      <Table
        dataSource={data}
        loading={loading}
        rowKey="id"
        pagination={false}
        scroll={{ x: 1100 }}
        data-testid="versions-table"
        locale={{
          emptyText: loading ? undefined : (
            <div style={{ padding: '40px 0' }}>
              <p>尚未設定平台版本</p>
            </div>
          ),
        }}
      >
        <Table.Column
          dataIndex="brand"
          title="品牌"
          width={100}
          render={(brand: Brand) => <Tag color="blue">{getBrandLabel(brand)}</Tag>}
        />
        <Table.Column
          dataIndex="platform"
          title="平台"
          width={130}
          render={(v: string) => PLATFORM_LABELS[v] || v}
        />
        <Table.Column dataIndex="minVersion" title="最低版本" width={120} />
        <Table.Column dataIndex="recommendedVersion" title="建議版本" width={120} />
        <Table.Column dataIndex="latestVersion" title="最新版本" width={120} />
        <Table.Column dataIndex="updateUrl" title="更新連結" width={250} ellipsis />
        <Table.Column
          title="操作"
          width={360}
          fixed="right"
          render={(_: unknown, record: VersionRecord) => (
            <Space>
              <Button
                size="small"
                icon={<BarChartOutlined />}
                onClick={() => openDist(record.platform)}
                data-testid="versions-distribution-btn"
                aria-label="查看版本分佈"
                // M4：版本分佈為單一品牌語義，聚合檢視下 fail-closed 禁用。
                disabled={isAllScope}
                title={isAllScope ? '請先切換到單一品牌檢視再查看版本分佈' : undefined}
              >
                分佈
              </Button>
              <Button size="small" onClick={() => setAdoptionRecord(record)} disabled={isAllScope}
                title={isAllScope ? '請先切換到單一品牌檢視' : undefined}>
                原生比例
              </Button>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(record)}
                data-testid="versions-edit-btn"
                aria-label="編輯版本"
                // M4：聚合檢視無法判定目標品牌 → 禁用編輯，引導切單一品牌檢視。
                disabled={isAllScope}
                title={isAllScope ? '請先切換到單一品牌檢視再編輯' : undefined}
              >
                編輯
              </Button>
              <Popconfirm
                title="確定刪除此版本設定？"
                onConfirm={() => handleDelete(record)}
                okText="確定"
                cancelText="取消"
                disabled={isAllScope}
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  data-testid="versions-delete-btn"
                  aria-label="刪除版本"
                  // M4：聚合檢視禁用刪除，引導切單一品牌檢視。
                  disabled={isAllScope}
                  title={isAllScope ? '請先切換到單一品牌檢視再刪除' : undefined}
                >
                  刪除
                </Button>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>

      {/* 編輯彈窗 — Sprint 3 P3-5 加 what-if preview */}
      <Modal
        title={
          <Space>
            編輯版本設定
            {editRecord ? (
              <Tag color="blue">{PLATFORM_LABELS[editRecord.platform] || editRecord.platform}</Tag>
            ) : null}
          </Space>
        }
        open={editOpen}
        onOk={handleEdit}
        onCancel={() => {
          setEditOpen(false);
          setWhatIfResult(null);
          editForm.resetFields();
        }}
        confirmLoading={editLoading}
        okText="確定"
        cancelText="取消"
        width={680}
      >
        <Alert
          type="info"
          showIcon
          message="提示"
          description={
            <span>
              <b>最低版本</b>變更會立即對 <code>&lt; minVersion</code> 的 client 推
              <code>force_update</code> (硬性,modal 蓋全屏)。
              <b>建議版本</b>變更只對 <code>&lt; recommendedVersion</code> 的 client 推
              <code>recommend_update</code> (可 dismiss banner)。下方 What-if Preview
              會即時試算「按目前的 minVersion 推下去會 force 多少 active user」。
            </span>
          }
          style={{ marginBottom: 16 }}
        />
        <Form
          form={editForm}
          layout="vertical"
          onValuesChange={(changed) => {
            if ('minVersion' in changed && editRecord) {
              const v = changed.minVersion;
              if (typeof v === 'string' && /^\d+\.\d+\.\d+$/.test(v)) {
                previewWhatIf(editRecord.platform, v);
              } else {
                setWhatIfResult(null);
              }
            }
          }}
        >
          <Form.Item
            name="minVersion"
            label={
              <Space>
                最低版本 (force_update 門檻)
                <Tag color="red">硬性</Tag>
              </Space>
            }
            rules={[{ required: true }, semverRule]}
          >
            <Input placeholder="1.0.0" />
          </Form.Item>

          {/* What-if preview block */}
          {whatIfResult ? (
            <Alert
              type={whatIfResult.forcedUserPercent > 50 ? 'warning' : 'success'}
              showIcon
              icon={
                whatIfResult.forcedUserPercent > 50 ? (
                  <WarningOutlined />
                ) : (
                  <ExperimentOutlined />
                )
              }
              message={
                <Space>
                  What-if Preview (過去 7 天 active client)
                  <Tag>candidate: {whatIfResult.candidateMinVersion}</Tag>
                </Space>
              }
              description={
                <Row gutter={16} style={{ marginTop: 8 }}>
                  <Col span={8}>
                    <Statistic
                      title="會被強制升級"
                      value={whatIfResult.forcedUserCount}
                      suffix={`/ ${whatIfResult.totalActiveUsers}`}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="影響比例"
                      value={whatIfResult.forcedUserPercent}
                      suffix="%"
                      valueStyle={{
                        color:
                          whatIfResult.forcedUserPercent > 50
                            ? '#cf1322'
                            : whatIfResult.forcedUserPercent > 20
                              ? '#d48806'
                              : '#3f8600',
                      }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="active version 數"
                      value={whatIfResult.breakdownByVersion.length}
                    />
                  </Col>
                </Row>
              }
              style={{ marginBottom: 16 }}
            />
          ) : whatIfLoading ? (
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              預覽計算中...
            </Text>
          ) : null}

          <Form.Item
            name="recommendedVersion"
            label={
              <Space>
                建議版本 (recommend_update 門檻)
                <Tag color="gold">軟性</Tag>
              </Space>
            }
            rules={[{ required: true }, semverRule]}
          >
            <Input placeholder="1.0.0" />
          </Form.Item>
          <Form.Item name="latestVersion" label="最新版本" rules={[{ required: true }, semverRule]}>
            <Input placeholder="1.0.0" />
          </Form.Item>
          <Form.Item name="updateUrl" label="更新連結">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="updateMessage" label="建議更新訊息 (recommend_update banner)">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="forceUpdateMessage" label="強制更新訊息 (force_update modal)">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {adoptionRecord && <NativeAdoption key={`${adoptionRecord.brand}-${adoptionRecord.platform}`}
        brand={adoptionRecord.brand} platform={adoptionRecord.platform} onClose={() => setAdoptionRecord(null)} />}
      {/* P3-5 Distribution 彈窗 */}
      <Modal
        title={
          <Space>
            版本分佈 — 過去 {distWindowDays} 天 active client
            <Tag color="blue">{PLATFORM_LABELS[distPlatform] || distPlatform}</Tag>
          </Space>
        }
        open={distOpen}
        onCancel={() => setDistOpen(false)}
        footer={null}
        width={720}
      >
        <Space style={{ marginBottom: 16 }}>
          <Text>窗口天數：</Text>
          <InputNumber
            min={1}
            max={90}
            value={distWindowDays}
            onChange={(v) => {
              const n = typeof v === 'number' && v > 0 ? Math.floor(v) : 7;
              setDistWindowDays(n);
              fetchDistribution(distPlatform, n);
            }}
          />
          <Button onClick={() => fetchDistribution(distPlatform, distWindowDays)}>
            重新整理
          </Button>
        </Space>
        <Table
          dataSource={distRows}
          loading={distLoading}
          rowKey={(r) =>
            `${r.appVersionCode ?? 'null'}-${r.appVersionName ?? 'null'}`
          }
          pagination={false}
          size="small"
          locale={{
            emptyText: distLoading
              ? undefined
              : '過去窗口內無 active client (或舊版皆未上報 version)',
          }}
        >
          <Table.Column
            dataIndex="appVersionName"
            title="版本名稱"
            render={(v: string | null) =>
              v == null ? <Tag color="red">未上報 (老 client)</Tag> : <code>{v}</code>
            }
          />
          <Table.Column
            dataIndex="appVersionCode"
            title="versionCode"
            render={(v: number | null) => (v == null ? '—' : v)}
          />
          <Table.Column
            dataIndex="uniqueUsers"
            title="unique users"
            sorter={(a: DistributionRow, b: DistributionRow) =>
              a.uniqueUsers - b.uniqueUsers
            }
          />
          <Table.Column
            dataIndex="eventCount"
            title="events"
            sorter={(a: DistributionRow, b: DistributionRow) =>
              a.eventCount - b.eventCount
            }
          />
        </Table>
      </Modal>
    </div>
  );
};
